import { TDOId } from '@common-modules/universal/models/Brands';
import { RUN_OCR_DEFAULT } from './ocrPreference';
export const namespace = 'vtn-redact-engine-ingestion';
export interface JobState {
  jobId?: string;
  isRunning: boolean;
  status:
    | 'pending'
    | 'queued'
    | 'running'
    | 'complete'
    | 'failed'
    | 'cancelled'
    | 'aborted';
  startedOn: Date;
  endedOn?: Date;
  error?: string;
}

export interface IngestionTDOs {
  [tdoId: TDOId]: {
    [jobId: string]: JobState;
  };
}

export interface IngestionStore {
  runHead: boolean;
  runPerson: boolean;
  runTranscription: boolean;
  /**
   * Speaker separation on ingest. Opt-in on its own: checking it checks
   * `runTranscription` too, since there is nothing to group without a
   * transcript, but transcribing does not opt you into separating.
   */
  runSpeakerSeparation: boolean;
  runVoiceMask: boolean;
  runPII: boolean;
  /**
   * Run the Redact Document OCR engine on ingested documents — independent of
   * `runPII`, so a document can be made searchable without PII detection.
   *
   * Unlike every other option here it starts TRUE and is remembered across
   * reloads; the FilePicker re-seeds it from `readRunOcrPreference()` each time
   * the dialog opens. See `ocrPreference.ts`.
   */
  runOCR: boolean;
  /**
   * The language the UPLOAD pickers will transcribe in — a preference that
   * follows the user, not a fact about any one piece of media.
   */
  transcriptionLanguage: string;
  /**
   * The language the audio tab's Run Engines flow will transcribe in, and the
   * TDO it was chosen for.
   *
   * Separate from the upload preference above because the two answer different
   * questions. Re-running an engine on media that already has a transcript
   * starts from THAT transcript's language — the dropdown used to open on
   * whatever was last uploaded, so a file transcribed in Spanish presented
   * itself as untranscribed whenever the preference said English, and the
   * reverse. Writing the choice back into the upload preference would push the
   * language of whatever media was last opened onto the next upload, which is
   * the same leak in the other direction.
   *
   * Carries its `tdoId` so it invalidates itself: a choice made for one piece
   * of media is not an answer for the next, and comparing ids needs no action
   * on TDO change to clear it.
   */
  mediaTranscription?: { tdoId: string; code: string };
  tdos: IngestionTDOs;
}

export const defaultState: IngestionStore = {
  runHead: false,
  runPerson: false,
  runTranscription: false,
  runSpeakerSeparation: false,
  runVoiceMask: false,
  runPII: false,
  // The stored preference wins over this the moment the dialog opens; this is
  // only what an upload path that never renders the dialog would get.
  runOCR: RUN_OCR_DEFAULT,
  transcriptionLanguage: 'en',
  tdos: {},
};

export const defaultJobSlice = (): JobState => ({
  jobId: undefined,
  isRunning: true,
  status: 'pending',
  startedOn: new Date(),
  endedOn: undefined,
  error: undefined,
});
